import { Controller } from "@hotwired/stimulus"

// A panel that sits inline on wider screens and becomes an off-canvas side
// menu below the `sm` breakpoint (the CSS does the layout switch, keyed off
// `data-open` on the panel and backdrop). While open as a side menu it
// behaves as a modal dialog:
//   - focus moves into it, Tab stays inside, Escape or the backdrop closes it
//     and focus returns to the trigger
//   - the page behind doesn't scroll
//   - `data-autosubmit-paused` holds autosubmitting selects back, so the
//     customer can set several filters and submit once
export default class extends Controller {
  static targets = ["panel", "backdrop", "trigger"]
  static values = { label: String }

  connect() {
    this.mobile = window.matchMedia("(max-width: 639.98px)")
    this.onBreakpoint = () => { if (!this.mobile.matches) this.close({ restoreFocus: false }) }
    this.mobile.addEventListener("change", this.onBreakpoint)
    this.onKeydown = this.keydown.bind(this)
    this.triggerTarget.setAttribute("aria-expanded", "false")
  }

  disconnect() {
    this.mobile.removeEventListener("change", this.onBreakpoint)
    this.close({ restoreFocus: false })
  }

  open() {
    if (!this.mobile.matches || this.isOpen) return

    this.panelTarget.dataset.open = ""
    this.backdropTarget.dataset.open = ""
    this.panelTarget.dataset.autosubmitPaused = ""
    this.panelTarget.setAttribute("role", "dialog")
    this.panelTarget.setAttribute("aria-modal", "true")
    this.panelTarget.setAttribute("aria-label", this.labelValue)
    this.triggerTarget.setAttribute("aria-expanded", "true")
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this.onKeydown)

    // Wait a frame so the panel is visible (and so focusable) first. Start on
    // the first field rather than the close button — that's why it was opened.
    requestAnimationFrame(() => {
      const items = this.focusables
      ;(items.find((el) => el.matches("input, select")) || items[0])?.focus()
    })
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return

    delete this.panelTarget.dataset.open
    delete this.backdropTarget.dataset.open
    delete this.panelTarget.dataset.autosubmitPaused
    for (const attr of ["role", "aria-modal", "aria-label"]) this.panelTarget.removeAttribute(attr)
    this.triggerTarget.setAttribute("aria-expanded", "false")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this.onKeydown)

    if (restoreFocus) this.triggerTarget.focus()
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      const items = this.focusables
      if (items.length === 0) return

      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  get isOpen() {
    return "open" in this.panelTarget.dataset
  }

  get focusables() {
    return [...this.panelTarget.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled])")]
      .filter((el) => el.offsetParent !== null)
  }
}
